"use client";

import { useCallback, useRef, useState } from "react";
import { WizardStep } from "../_types/wizard-types";
import { WIZARD_STEPS } from "../_logic/constants";

interface UseWizardNavigationOptions {
  initialCurrentStep?: WizardStep;
  initialCompletedSteps?: Set<WizardStep>;
  initialIsComplete?: boolean;
}

/** Removes every step from `targetIdx` onward from a completed-steps set — used when navigating
 *  backward so previously-completed later steps stop showing as done. */
function clearFrom(prev: Set<WizardStep>, targetIdx: number): Set<WizardStep> {
  const next = new Set(prev);
  for (let i = targetIdx; i < WIZARD_STEPS.length; i++) {
    next.delete(WIZARD_STEPS[i].id);
  }
  return next;
}

/**
 * Manages step navigation, direction tracking, and completion state for the wizard.
 * Uses refs to avoid stale closures in navigation callbacks during animations.
 *
 * @param options - Optional initial state for resuming the wizard after an
 *   auth round-trip. When `initialIsComplete` is true, callers should also
 *   pass the full set of step ids as `initialCompletedSteps` and the last
 *   step id as `initialCurrentStep` so `goBack` lands sensibly.
 * @returns Navigation state and control functions.
 */
export function useWizardNavigation(options?: UseWizardNavigationOptions): {
  currentStep: WizardStep;
  currentIndex: number;
  direction: 1 | -1;
  completedSteps: Set<WizardStep>;
  isComplete: boolean;
  goForward: () => void;
  goBack: () => void;
  goToStep: (step: WizardStep) => void;
} {
  // States
  const [currentStep, setCurrentStep] = useState<WizardStep>(options?.initialCurrentStep ?? WIZARD_STEPS[0].id);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(options?.initialCompletedSteps ?? new Set());
  const [isComplete, setIsComplete] = useState(options?.initialIsComplete ?? false);

  // Refs — keep callbacks stable and avoid stale closures
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  const completedStepsRef = useRef(completedSteps);
  completedStepsRef.current = completedSteps;

  // Derived values
  const currentIndex = WIZARD_STEPS.findIndex((s) => s.id === currentStep);

  // Handlers
  const goForward = useCallback(() => {
    const step = currentStepRef.current;
    const idx = WIZARD_STEPS.findIndex((s) => s.id === step);
    const isLastStep = idx === WIZARD_STEPS.length - 1;

    setCompletedSteps((prev) => new Set([...prev, step]));

    if (isLastStep) {
      setIsComplete(true);
      return;
    }

    setDirection(1);
    setCurrentStep(WIZARD_STEPS[idx + 1].id);
  }, []);

  const goBack = useCallback(() => {
    const step = currentStepRef.current;
    const idx = WIZARD_STEPS.findIndex((s) => s.id === step);
    if (idx <= 0) return;

    const targetIdx = idx - 1;
    setDirection(-1);
    setCurrentStep(WIZARD_STEPS[targetIdx].id);
    setCompletedSteps((prev) => clearFrom(prev, targetIdx));
  }, []);

  const goToStep = useCallback((step: WizardStep) => {
    if (!completedStepsRef.current.has(step)) return;

    const targetIndex = WIZARD_STEPS.findIndex((s) => s.id === step);
    const currentIdx = WIZARD_STEPS.findIndex((s) => s.id === currentStepRef.current);
    setDirection(targetIndex < currentIdx ? -1 : 1);
    setCurrentStep(step);
    setIsComplete(false);
    if (targetIndex < currentIdx) {
      setCompletedSteps((prev) => clearFrom(prev, targetIndex));
    }
  }, []);

  return {
    currentStep,
    currentIndex,
    direction,
    completedSteps,
    isComplete,
    goForward,
    goBack,
    goToStep,
  };
}
